import {Trans} from '@lingui/macro';
import {isNull} from 'lodash';
import React, {FC, useCallback, useEffect, useState} from 'react';
import styled, {css} from 'styled-components';

import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserTheme, useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {usePrevious, useTimeoutChain, useToggle} from '../../../../../../srcCommon/helpers/hookHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {ConversationSummaryContainer} from '../conversationSummary/conversationSummaryContainer';
import {ConversationSummarySectionsEnum} from '../conversationSummary/conversationSummarySections';

/*
 * Props and Types.
 */

interface ConversationPinnedActivitiesButtonProps {
  panelId: ViewerPanelId;
  conversation: ConversationModel;
  teammateId: number;
  className?: string;
}

enum AnimationStatesEnum {
  NORMAL = 'NORMAL',
  JUST_PINNED = 'JUST_PINNED',
  JUST_PINNED_OUTRO = 'JUST_PINNED_OUTRO',
}

/*
 * Styles.
 */

// Animation length constants
const animationDelayMs = 150;
const fadeAnimationDurationMs = 250;
const justPinnedStateDurationMs = 5000;

interface PinnedButtonStyleProps {
  $animationState: AnimationStatesEnum;

  $backgroundColor: string;
  $borderColor: string;

  $hoverBackgroundColor: string;
  $hoverBorderColor: string;
}

const StyledPinnedButton = styled(ChromelessButton)<PinnedButtonStyleProps>`
  background-color: ${(p) => p.$backgroundColor};
  border-radius: 100px;
  display: flex;
  align-items: center;
  margin-left: 1px;
  margin-right: 8px;
  border: ${(p) => p.$borderColor} 1px solid;

  ${(p) => maybeAddTransition(p)}

  &:hover {
    transition: none;
    background-color: ${(p) => p.$hoverBackgroundColor};
    border-color: ${(p) => p.$hoverBorderColor};
  }
`;

function maybeAddTransition(props: PinnedButtonStyleProps) {
  if (props.$animationState === AnimationStatesEnum.NORMAL) {
    return null;
  }

  return css`
    transition: background-color ${fadeAnimationDurationMs}ms ease-out,
      border-color ${fadeAnimationDurationMs}ms ease-out;
  `;
}

const StyledPinnedButtonIconDiv = styled.div`
  margin-top: -1px;
  margin-left: 6px;
  margin-right: 3px;
`;

interface NumPinnedStyleProps {
  $color: string;
}
const StyledNumPinnedSpan = styled.span<NumPinnedStyleProps>`
  color: ${(p) => p.$color};
  font-size: 12px;
  font-weight: ${fontWeights.semibold};
  padding: 2px 7px 2px 0px;
  transition: color ${fadeAnimationDurationMs}ms ease-out;
`;

const StyledAnimatedIcon = styled(Icon)`
  transition: color ${fadeAnimationDurationMs}ms ease-out;
`;

const makeNormalColors = (theme: UserTheme) => ({
  backgroundColor: theme.palette.grey.shade10,
  hoverBackgroundColor: theme.palette.grey.shade20,
  hoverBorderColor: theme.palette.grey.shade50,
  borderColor: theme.palette.grey.shade40,
  textColor: theme.palette.grey.shade40,
  iconColor: theme.palette.grey.shade40,
});

const makeJustPinnedColors = (theme: UserTheme) => ({
  backgroundColor: theme.palette.blue.shade40,
  hoverBackgroundColor: theme.palette.blue.shade50,
  hoverBorderColor: theme.palette.blue.shade50,
  borderColor: theme.palette.blue.shade40,
  textColor: theme.greys.white,
  iconColor: theme.greys.white,
});

/*
 * Component.
 */

export const ConversationPinnedActivitiesButton: FC<ConversationPinnedActivitiesButtonProps> = (props) => {
  const [isConversationSummaryOpen, toggleIsConversationSummaryOpen] = useToggle();
  const [animationState, setAnimationState] = useJustPinnedAnimation(
    props.conversation.id,
    props.conversation.numPinnedActivities,
  );
  const theme = useUserTheme();
  const wasJustPinned = animationState === AnimationStatesEnum.JUST_PINNED; // Convenience helper.

  const regularColors = makeNormalColors(theme);
  const justPinnedColors = makeJustPinnedColors(theme);

  // Immediately disable our animation states so we stop showing the blue icon when the user clicks it.
  const onClick = useCallback(() => {
    setAnimationState(AnimationStatesEnum.NORMAL);

    toggleIsConversationSummaryOpen();
  }, [setAnimationState, toggleIsConversationSummaryOpen]);

  const maybeRenderConversationSummary = () =>
    isConversationSummaryOpen ? (
      <ConversationSummaryContainer
        conversation={props.conversation}
        teammateId={props.teammateId}
        onRequestClose={toggleIsConversationSummaryOpen}
        initiallyExpandedSection={ConversationSummarySectionsEnum.PINNED_ACTIVITIES}
        panelId={props.panelId}
      />
    ) : null;

  const pinnedButtonProps = {
    ...(wasJustPinned ? justPinnedColors : regularColors),
    animationState,
  };

  // This check (props.conversation.numPinnedActivities > 0) was originally outside this component and conditionally
  // rendering the entire thing.
  // It was necessary to bring it inside the component because of a reliance on `usePrevious(numPinnedActivities)`.
  //
  // During a render, the value for the previous `numPinnedActivities` would be null
  // in both of the following scenarios if this check was performed outside of the component.
  //    1. numPinnedActivities is 0 and goes to 1 when the user pins a comment.
  //    2. The user loads a conversation and numPinnedActivities is already 1.
  const maybeRenderConversationPinnedActivitiesButton = () =>
    props.conversation.numPinnedActivities ? (
      <TooltipCoordinator
        render={() => <Trans id="conversation-header-pinned-activities-button">Pinned Comments</Trans>}
        className={props.className}
      >
        <StyledPinnedButton
          $animationState={pinnedButtonProps.animationState}
          $backgroundColor={pinnedButtonProps.backgroundColor}
          $borderColor={pinnedButtonProps.borderColor}
          $hoverBackgroundColor={pinnedButtonProps.hoverBackgroundColor}
          $hoverBorderColor={pinnedButtonProps.hoverBorderColor}
          onClick={onClick}
          interactionId="conversationPinnedActivitiesButton"
        >
          <StyledPinnedButtonIconDiv>
            <StyledAnimatedIcon
              name="pinFilledTiny"
              size={15}
              color={wasJustPinned ? justPinnedColors.iconColor : regularColors.iconColor}
            />
          </StyledPinnedButtonIconDiv>

          <StyledNumPinnedSpan $color={wasJustPinned ? justPinnedColors.textColor : regularColors.textColor}>
            {props.conversation.numPinnedActivities}
          </StyledNumPinnedSpan>
        </StyledPinnedButton>
      </TooltipCoordinator>
    ) : null;

  // NOTE: The button can exist without the Conversation Summary modal and vice versa.
  return (
    <>
      {maybeRenderConversationPinnedActivitiesButton()}
      {maybeRenderConversationSummary()}
    </>
  );
};

/*
 * Helpers.
 */

// This is an animation chain that gets kicked off if the numPinnedActivities increases.
//    Step 1: Delay for `animationDelayMs` to let the "pin comment" click handler do it's thing since it blocks
//            the UI for like a quarter second updating the P3 layout.
//
//    Step 2: Set animationState to JUST_PINNED for `justPinnedStateDuration` seconds.
//            (This lets us fade the color of the button to blue, and then keep it there for awhile).
//
//    Step 3: Set animationState to JUST_PINNED_OUTRO for `fadeAnimationDurationMs` seconds.
//            (This lets us fade out the blue color out completely before disabling the CSS transition).
//
//    Step 4: Set the animationState back to NORMAL.
//
// NOTE: Normally we'd use React Spring for an animation here, but it wasn't performant enough in P3, so we needed to
//       fallback on trusty CSS transitions and state changes.
const useJustPinnedAnimation = (
  conversationId: number,
  numPinnedActivities: number,
): [AnimationStatesEnum, React.Dispatch<React.SetStateAction<AnimationStatesEnum>>] => {
  const setTimeoutChain = useTimeoutChain();
  const [animationState, setAnimationState] = useState(AnimationStatesEnum.NORMAL);
  const previousNumPinnedActivities = usePrevious(numPinnedActivities);
  const previousConversationId = usePrevious(conversationId); // Need to track this so we dont fire the animation when switching conversations.

  useEffect(() => {
    if (
      isNull(previousConversationId) ||
      isNull(previousNumPinnedActivities) ||
      numPinnedActivities <= previousNumPinnedActivities ||
      previousConversationId !== conversationId
    ) {
      return;
    }

    setTimeoutChain([
      [
        () => {
          setAnimationState(AnimationStatesEnum.JUST_PINNED);
        },
        animationDelayMs,
      ],
      [
        () => {
          setAnimationState(AnimationStatesEnum.JUST_PINNED_OUTRO);
        },
        justPinnedStateDurationMs,
      ],
      [
        () => {
          setAnimationState(AnimationStatesEnum.NORMAL);
        },
        fadeAnimationDurationMs,
      ],
    ]);
  }, [
    setTimeoutChain,
    setAnimationState,
    previousNumPinnedActivities,
    numPinnedActivities,
    previousConversationId,
    conversationId,
  ]);

  return [animationState, setAnimationState];
};
